import { useCallback, useState } from "react";
import { ButtonBase, useTheme } from "@mui/material";
import {
  LockOutlined as LockIcon,
  NotificationsActiveOutlined as AlertsOnIcon,
  NotificationsNoneOutlined as AlertsOffIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAnalytics } from "../../hooks/useAnalytics";
import { alertGold } from "../Dashboard/overviewPanel";
import { hapticError, hapticSelection, hapticSuccess } from "../../lib/haptics";
import { isNative } from "../../lib/platform";
import { claimPushSoftAsk } from "../../lib/push";
import AlertLevelDialog from "./AlertLevelDialog";
import PushSoftAskSheet from "./PushSoftAskSheet";
import { useMySubscriptions } from "./hooks/useMySubscriptions";
import { useSubscribe } from "./hooks/useSubscribe";
import { useUnsubscribe } from "./hooks/useUnsubscribe";
import { findSubscriptionForEntity } from "./subscriptionSelectors";
import { MySubscriptions, SubscriptionLevel } from "./types";

/** Where the Pro upgrade lives on web: the Lemon Squeezy change-plan flow. */
const UPGRADE_PATH = "/auth/settings";
const START_MEMBERSHIP_PATH = "/start-free-trial";

interface SubscribeButtonProps {
  topic: string;
  entityKind: string;
  /** The label the button was pressed on (for a ticker: that ticker). */
  entityKey: string;
  /**
   * The stable identity the SERVER resolved for this entity (for a ticker: the
   * company CIK from the company insider route). The on/off state is matched on
   * this, never on `entityKey` — the UI does not derive identities itself.
   */
  entityRef: string | null;
  /** Display name for the optimistic row. */
  label?: string | null;
  /** Which surface the button sits on, for analytics. */
  source: string;
}

/**
 * "Get alerts" / "Alerts on" for one subscribable thing.
 *
 * The CALLER decides whether alerts can exist at all (for insider trades: the
 * company route's server-derived `canSubscribe`) and only then mounts this.
 * What this component decides is what THIS USER may do, from the server's
 * `topicAccess` — it never computes a tier:
 *
 * - `allowed` → the live control, with the level picker;
 * - web, not allowed → a locked pill that leads to the upgrade (Pro) or to
 *   starting a membership;
 * - native, not allowed → nothing. Native sells one tier, so there is no
 *   locked state to show, and a web billing surface must never be reachable
 *   from it (App Store rule 3.1.1).
 */
const SubscribeButton = ({
  topic,
  entityKind,
  entityKey,
  entityRef,
  label,
  source,
}: SubscribeButtonProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const { capture } = useAnalytics();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pushSoftAskOpen, setPushSoftAskOpen] = useState(false);

  // One cached list for the whole app; this button only selects from it.
  const select = useCallback(
    (data: MySubscriptions) => ({
      access: data.topicAccess[topic],
      subscription: findSubscriptionForEntity(data.subscriptions, {
        topic,
        entityKind,
        entityKey,
        entityRef,
      }),
    }),
    [topic, entityKind, entityKey, entityRef],
  );
  const { data } = useMySubscriptions(select);
  const subscribe = useSubscribe();
  const unsubscribe = useUnsubscribe();

  // No answer yet, or a topic the server did not rule on: show nothing rather
  // than guess at an upsell.
  if (!data?.access) return null;

  const { access, subscription } = data;
  const isAllowed = access === "allowed";
  if (!isAllowed && isNative()) return null;

  const isOn = Boolean(subscription);
  const isPending = subscribe.isPending || unsubscribe.isPending;
  const callbacks = { onSuccess: () => hapticSuccess(), onError: () => hapticError() };

  const handleLockedClick = () => {
    hapticSelection();
    capture("alert_upgrade_clicked", { topic, source, access });
    navigate(access === "PRO_REQUIRED" ? UPGRADE_PATH : START_MEMBERSHIP_PATH);
  };

  const handleSelectLevel = (level: SubscriptionLevel) => {
    setDialogOpen(false);
    if (subscription?.level === level) return;
    capture(isOn ? "alert_level_changed" : "alert_subscribed", { topic, source, level });
    subscribe.mutate(
      { topic, entityKind, entityKey, entityRef, label, level },
      {
        ...callbacks,
        onSuccess: () => {
          hapticSuccess();
          // A new alert is the one moment push is worth asking for. The claim
          // is true once per install, on native, while iOS is still unasked.
          if (isOn) return;
          void claimPushSoftAsk().then((claimed) => {
            if (claimed) setPushSoftAskOpen(true);
          });
        },
      },
    );
  };

  const handleTurnOff = () => {
    setDialogOpen(false);
    if (!subscription) return;
    capture("alert_unsubscribed", { topic, source });
    unsubscribe.mutate({ id: subscription.id }, callbacks);
  };

  const onColor = theme.palette.primary.main;
  const restingBorder = theme.palette.divider;
  const hoverWash = isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.04)";
  // Gold means alerts app-wide: a gold bell at rest, a gold outline and wash
  // once they are on. Green stays for primary actions and selection.
  const gold = alertGold(isDark);

  // The house pill: the same rail as the workspace switch it sits beside.
  const pillSx = {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.75,
    flexShrink: 0,
    minHeight: 34,
    px: 1.25,
    borderRadius: 1,
    border: `1px solid ${isOn ? gold.outline : restingBorder}`,
    backgroundColor: isOn ? gold.wash : "background.surface",
    color: "text.primary",
    fontFamily: "inherit",
    fontSize: "0.8125rem",
    fontWeight: isOn ? 600 : 500,
    lineHeight: 1.4,
    whiteSpace: "nowrap",
    WebkitTapHighlightColor: "transparent",
    transition: "transform 0.1s ease, background-color 0.15s ease, border-color 0.15s ease",
    "@media (pointer: coarse)": { minHeight: 44 },
    "&:hover": {
      backgroundColor: isOn ? gold.washHover : hoverWash,
      borderColor: isOn ? gold.outline : restingBorder,
    },
    "&:active": { transform: "scale(0.98)" },
    "&:focus-visible": { outline: `2px solid ${onColor}`, outlineOffset: 2 },
    "&.Mui-disabled": { opacity: 0.5 },
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none",
      "&:active": { transform: "none" },
    },
  } as const;

  if (!isAllowed) {
    return (
      <ButtonBase
        onClick={handleLockedClick}
        aria-label="Get alerts — available with Insight Pro"
        sx={{ ...pillSx, color: "text.secondary" }}
      >
        <LockIcon sx={{ fontSize: 16 }} />
        Get alerts · Pro
      </ButtonBase>
    );
  }

  return (
    <>
      <ButtonBase
        onClick={() => {
          hapticSelection();
          setDialogOpen(true);
        }}
        disabled={isPending}
        aria-haspopup="dialog"
        sx={{
          ...pillSx,
          ...(!isOn && {
            "&:hover": { backgroundColor: gold.wash, borderColor: gold.outline },
          }),
        }}
      >
        {isOn && <AlertsOnIcon sx={{ fontSize: 16, color: gold.glyph }} />}
        {!isOn && <AlertsOffIcon sx={{ fontSize: 16, color: gold.glyph }} />}
        {isOn && "Alerts on"}
        {!isOn && "Get alerts"}
      </ButtonBase>

      <AlertLevelDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        topic={topic}
        subject={label ?? entityKey}
        level={subscription?.level ?? null}
        onSave={handleSelectLevel}
        onTurnOff={handleTurnOff}
      />

      {isNative() && (
        <PushSoftAskSheet open={pushSoftAskOpen} onClose={() => setPushSoftAskOpen(false)} />
      )}
    </>
  );
};

export default SubscribeButton;
